import { CampaignPerformanceMetrics } from "@/components/campaign/campaign-performance-metrics";
import { CampaignPlacementBadges } from "@/components/campaign/campaign-placement-badges";
import { SpendProgress } from "@/components/campaign/spend-progress";
import { CardBadge } from "@/components/shared/card-badge";
import { useCampaign } from "@/hooks/use-campaign";
import {
  findCampaignRuleValues,
  type ApiCampaignRecord,
  type CampaignAdPlacement,
} from "@/services/campaigns.service";
import {
  formatCampaignCurrencyFromMilliDollars,
  formatCampaignDateLabel,
  getCampaignStatusLabel,
  getMarket,
} from "@/utils/campaigns";
import { getPlacementChannelLabel } from "@/utils/placements";
import { getInitials } from "@/utils/strings";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  Skeleton,
} from "@getgreenline/blaze-ui";
import { ArrowLeft, CircleX, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

function DetailStat({
  label,
  value,
  labelIcon,
  color,
}: {
  label: string;
  labelIcon?: ReactNode;
  value: ReactNode | string;
  color?: string;
}) {
  return (
    <div className="tw:rounded-lg tw:border tw:border-border/70 tw:bg-muted/80 tw:px-4 tw:py-3">
      <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-xs tw:font-medium tw:text-muted-foreground">
        <span>{label}</span>
      </div>
      <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-foreground">
        {labelIcon ? (
          <span className="tw:text-muted-foreground/80">{labelIcon}</span>
        ) : null}
        <div
          className="tw:break-words tw:text-base tw:font-semibold tw:text-foreground sm:tw:text-lg"
          style={{ color: color }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-[0.16em] tw:text-muted-foreground">
      {children}
    </h2>
  );
}

function groupPlacementsByChannel(adPlacements: CampaignAdPlacement[]) {
  const placementsByChannel = new Map<
    CampaignAdPlacement["adPlacement"]["channel"],
    string[]
  >();

  adPlacements.forEach(({ adPlacement }) => {
    const placements = placementsByChannel.get(adPlacement.channel) ?? [];

    placements.push(adPlacement.title ?? adPlacement.zoneId);
    placementsByChannel.set(adPlacement.channel, placements);
  });

  return Array.from(placementsByChannel, ([channel, placements]) => {
    return {
      channel,
      channelLabel: getPlacementChannelLabel(channel),
      placements,
    };
  });
}

function formatCampaignMetadata(campaign: ApiCampaignRecord): string {
  const eligibleStates = findCampaignRuleValues(
    campaign.eligibilityFilter,
    "STATE",
  );

  if (!campaign.endDate)
    return `${getMarket(eligibleStates?.[0])} | 
        ${formatCampaignDateLabel(campaign.startDate) ?? "Not set"}`;

  return `${getMarket(eligibleStates?.[0])} | 
      ${formatCampaignDateLabel(campaign.startDate) ?? "Not set"} - ${
        formatCampaignDateLabel(campaign.endDate) ?? "Not set"
      }`;
}

function toCampaignDetailsView(campaign: ApiCampaignRecord) {
  const minPrice = findCampaignRuleValues(
    campaign.productFilter,
    "PRICE",
    "GREATER_OR_EQUAL",
  )?.[0];
  const dailyPacingCapMilliDollars = campaign.pacingRules.find(
    (rule) => rule.window === "DAY",
  )?.maxCredits;

  return {
    budgetLabel: formatCampaignCurrencyFromMilliDollars(campaign.budget),
    ctrLabel: `${campaign.ctr.toFixed(2)}%`,
    dailyPacingCapMilliDollars,
    impressionsLabel: campaign.impressions.toLocaleString(),
    metadata: formatCampaignMetadata(campaign),
    placementGroups: groupPlacementsByChannel(campaign.adPlacements),
    productMinPriceLabel: minPrice
      ? formatCampaignCurrencyFromMilliDollars(Number(minPrice) * 1000)
      : "N/A",
    spendLabel: formatCampaignCurrencyFromMilliDollars(campaign.spend),
    statusLabel: getCampaignStatusLabel(campaign.status),
  };
}

export function CampaignDetailsPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const campaignQuery = useCampaign(campaignId);
  const campaign = campaignQuery.data;

  if (campaignQuery.isLoading) {
    return (
      <main className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4 tw:px-6 tw:py-8 sm:tw:px-8">
        <Skeleton className="tw:h-10 tw:w-48" />
        <Skeleton className="tw:h-40 tw:w-full" />
        <Skeleton className="tw:h-60 tw:w-full" />
      </main>
    );
  }

  if (campaignQuery.isError) {
    return (
      <main className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4 tw:px-6 tw:py-8 sm:tw:px-8">
        <Empty>
          <EmptyMedia variant="icon">
            <CircleX className="tw:size-12" />
          </EmptyMedia>
          <EmptyHeader className="tw:font-semibold">
            Error fetching campaign{" "}
          </EmptyHeader>
          <EmptyContent>
            The requested campaign could not be loaded.
            <Button onClick={() => navigate("/campaigns")}>
              <ArrowLeft />
              Go back to campaign list
            </Button>
          </EmptyContent>
        </Empty>
      </main>
    );
  }

  if (!campaign) {
    return (
      <main className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4 tw:px-6 tw:py-8 sm:tw:px-8">
        <Empty>
          <EmptyMedia variant="icon">
            <SearchX className="tw:size-12" />
          </EmptyMedia>
          <EmptyHeader className="tw:font-semibold">
            Campaign not found
          </EmptyHeader>
          <EmptyContent>
            The requested campaign could not be found.
            <Button onClick={() => navigate("/campaigns")}>
              <ArrowLeft />
              Go back to campaign list
            </Button>
          </EmptyContent>
        </Empty>
      </main>
    );
  }

  const campaignDetails = toCampaignDetailsView(campaign);

  return (
    <main className="tw:@container tw:flex tw:min-w-0 tw:flex-col tw:gap-6 tw:px-6 tw:py-8 sm:tw:px-8">
      <div className="tw:flex tw:items-center tw:gap-4">
        <Avatar className="tw:h-10 tw:w-10 tw:shrink-0">
          <AvatarImage
            src={campaign.brand?.avatarImageUrl ?? undefined}
            alt={campaign.brand?.name ?? ""}
          />
          <AvatarFallback className="tw:text-xs tw:font-semibold">
            {getInitials(campaign.name)}
          </AvatarFallback>
        </Avatar>
        <div className="tw:min-w-0 tw:space-y-1">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <h1 className="tw:text-2xl tw:font-semibold tw:leading-tight tw:text-foreground">
              {campaign.name}
            </h1>
            {/* TODO replace this with CampaignStatusBadge */}
            <CardBadge variant="default">
              {campaignDetails.statusLabel}
            </CardBadge>
          </div>
          <p className="tw:text-sm tw:text-muted-foreground">
            {campaignDetails.metadata}
          </p>
        </div>
      </div>

      <SectionHeading>Performance</SectionHeading>
      <CampaignPerformanceMetrics
        spend={campaignDetails.spendLabel}
        impressions={campaignDetails.impressionsLabel}
        ctr={campaignDetails.ctrLabel}
        className="tw:gap-4"
      />
      <SpendProgress
        spendMilliDollars={campaign.spend}
        budgetMilliDollars={campaign.budget}
        spendLabel={campaignDetails.spendLabel}
        budgetLabel={campaignDetails.budgetLabel}
      ></SpendProgress>

      <SectionHeading>Campaign</SectionHeading>
      <div className="tw:grid tw:grid-cols-2 tw:gap-3">
        <DetailStat
          label="Brand"
          value={campaign.brand?.name ?? "Failed to lookup brand"}
          color={campaign?.brand?.name ? undefined : "crimson"}
        />
        <DetailStat label="Ad format" value={"Sponsored products"} />
        <DetailStat label="Campaign name" value={campaign.name} />
      </div>

      <SectionHeading>Products</SectionHeading>
      <DetailStat
        label="Minimum price"
        value={campaignDetails.productMinPriceLabel}
      ></DetailStat>

      <SectionHeading>Placements</SectionHeading>
      {campaignDetails.placementGroups.map(
        ({ channel, channelLabel, placements }) => {
          return (
            <DetailStat
              key={channel}
              label={channelLabel}
              value={<CampaignPlacementBadges placements={placements} />}
            />
          );
        },
      )}
      <SectionHeading>Budget</SectionHeading>
      <div className="tw:grid tw:grid-cols-2 tw:gap-4 md:tw:grid-cols-2">
        <DetailStat label="Total budget" value={campaignDetails.budgetLabel} />
        {campaignDetails.dailyPacingCapMilliDollars !== undefined && (
          <DetailStat
            label="Daily pacing cap"
            value={`~ ${formatCampaignCurrencyFromMilliDollars(
              campaignDetails.dailyPacingCapMilliDollars,
            )} / day`}
          />
        )}
        <DetailStat label="Pricing model" value={campaign.type} />
      </div>
    </main>
  );
}
